import { createSelector } from '@reduxjs/toolkit';
import keyBy from 'lodash/keyBy';
import { isInsideDeviceGeofence } from '@artemis/utils/location';
import { getCart } from '../cart/selectors';

const getIncentivesRoot = state => state.incentives;

export const getIncentiveGroups = createSelector(
  getIncentivesRoot,
  incentives => incentives?.data?.incentiveGroups || [],
);

export const getIncentivesList = createSelector(getIncentiveGroups, groups =>
  groups
    .map(group => group.incentives)
    .reduce(
      (allIncentives, incentives) => [...allIncentives, ...incentives],
      [],
    ),
);

export const getIncentivesById = createSelector(getIncentivesList, incentives =>
  keyBy(incentives, 'incentiveId'),
);

export const getGroupsByIncentiveId = createSelector(
  getIncentiveGroups,
  groups => {
    const result = {};
    groups.forEach(group => {
      group.incentives.forEach(incentive => {
        result[incentive.incentiveId] = group;
      });
    });

    return result;
  },
);

export const getTotalIncentivesCount = createSelector(
  getIncentiveGroups,
  groups => {
    let total = 0;
    groups.forEach(group => {
      total += group?.incentives?.length || 0;
    });

    return total;
  },
);

export const getIsLoading = createSelector(
  getIncentivesRoot,
  incentives => incentives.isFetching || incentives.isUpdating,
);

export const getCartIncentives = createSelector(
  getCart,
  cart => cart?.incentives || [],
);

export const getSelectedIncentives = createSelector(
  getCartIncentives,
  getIncentivesById,
  (cartIncentives, incentivesById) =>
    cartIncentives.map(incentive => ({
      ...incentivesById[incentive.incentiveId],
      ...incentive,
    })),
);

export const getCartInvalidIncentives = createSelector(
  getCartIncentives,
  cartIncentives => cartIncentives.filter(incentive => !incentive.valid),
);

export const getIsListModalOpen = createSelector(
  getIncentivesRoot,
  incentives => incentives.listModal.isOpen,
);

export const getIsListModalSelectable = createSelector(
  getIncentivesRoot,
  incentives => incentives.listModal.isSelectable,
);

const getDetailsModal = createSelector(
  getIncentivesRoot,
  incentives => incentives.detailsModal,
);

export const getIsDetailsModalOpen = createSelector(
  getDetailsModal,
  detailsModal => detailsModal.isOpen,
);

export const getIncentiveForDetailsModal = createSelector(
  getDetailsModal,
  getIncentivesById,
  (detailsModal, incentivesById) => incentivesById[detailsModal.incentiveId],
);

export const getDeviceLocation = createSelector(
  getIncentivesRoot,
  incentives => incentives.deviceLocation.coords,
);

export const getIsDeviceLocationDisabled = createSelector(
  getIncentivesRoot,
  incentives => incentives.deviceLocation.disabled,
);

// True when at least one selected incentive requires the device's location.
export const getNeedsDeviceLocation = createSelector(
  getSelectedIncentives,
  selectedIncentives =>
    selectedIncentives.some(
      incentive => incentive.deviceLocationIncentiveRuleInfo,
    ),
);

export const getIsInsideDeviceGeofence = createSelector(
  getDeviceLocation,
  getSelectedIncentives,
  (deviceLocation, selectedIncentives) =>
    isInsideDeviceGeofence(deviceLocation, selectedIncentives),
);

// Warn the user to enable location services when a selected incentive needs
// the device location but we couldn't obtain it.
export const getShouldShowEnableLocationWarning = createSelector(
  getNeedsDeviceLocation,
  getIsDeviceLocationDisabled,
  (needsLocation, locationDisabled) => needsLocation && locationDisabled,
);

// Warn the user they are outside the geofence only once we actually have a
// device location to compare against.
export const getShouldShowOutsideGeofenceWarning = createSelector(
  getNeedsDeviceLocation,
  getDeviceLocation,
  getIsInsideDeviceGeofence,
  (needsLocation, deviceLocation, insideGeofence) =>
    needsLocation && !!deviceLocation && !insideGeofence,
);
